import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { accessToken } from '@/lib/api/http';
import { fetchVaultClaim, fetchVaultStatus, type VaultStatus } from '@/lib/api/vault';
import { useRouter } from '@/lib/router';
import { STORAGE_KEYS, storage } from '@/lib/storage/keys';
import { abortableWait, watchClaim } from './claimWatch';
import { FunnelContext, type FunnelValue } from './context';
import { mergeClaim, parseCheckoutPending, revealedRungs, signature, vaultDocument } from './model';

/** FN-D13: a window focus re-reads the status at most this often. */
const FOCUS_REFRESH_MIN_MS = 15_000;

function readPending() {
  const { pending, expired } = parseCheckoutPending(storage.get(STORAGE_KEYS.checkoutPending), Date.now());
  if (expired) storage.remove(STORAGE_KEYS.checkoutPending);
  return pending;
}

/**
 * Holds the visitor's place on the vault ladder for the whole app (mount above the router outlet
 * and the nav, inside RouterProvider and AuthProvider). Status is read on mount, on every pathname
 * change, when the stored token changes, on a bfcache restore and on window focus (throttled).
 * After the first read — and again after a bfcache restore, which is a new page view — an
 * outstanding checkout is claimed (FN-D2: one polling loop per page view).
 */
export function FunnelProvider({ children }: { readonly children: ReactNode }) {
  const { pathname } = useRouter();
  const { token: sessionToken } = useAuth();
  const [status, setStatus] = useState<VaultStatus | null>(null);
  const inflight = useRef<Promise<VaultStatus | null> | null>(null);
  const lastToken = useRef<string | null | undefined>(undefined);
  const lastPath = useRef<string | null>(null);
  const lastRequestAt = useRef(0);
  const claimLoop = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  const refresh = useCallback((): Promise<VaultStatus | null> => {
    if (inflight.current !== null) return inflight.current;
    lastToken.current = accessToken();
    lastRequestAt.current = Date.now();
    const request = fetchVaultStatus().then((next) => {
      inflight.current = null;
      if (next !== null && mounted.current) setStatus(next); // FN-D15: failures never replace a good status
      return next;
    });
    inflight.current = request;
    return request;
  }, []);

  const startClaimWatch = useCallback(() => {
    if (claimLoop.current !== null) return;
    const controller = new AbortController();
    claimLoop.current = controller;
    void watchClaim(
      {
        pending: readPending,
        hasToken: () => accessToken() !== null,
        claim: () => fetchVaultClaim(true),
        onClaim: (claim) => {
          if (mounted.current) setStatus((previous) => mergeClaim(previous, claim)); // FN-D1
        },
        clearPending: () => {
          storage.remove(STORAGE_KEYS.checkoutPending);
        },
        wait: abortableWait,
      },
      controller.signal,
    ).finally(() => {
      if (claimLoop.current === controller) claimLoop.current = null;
    });
  }, []);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    void refresh().then(() => {
      if (!cancelled) startClaimWatch();
    });
    const onFocus = () => {
      if (Date.now() - lastRequestAt.current >= FOCUS_REFRESH_MIN_MS) void refresh();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      void refresh().then(() => {
        if (!cancelled) startClaimWatch();
      });
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      cancelled = true;
      mounted.current = false;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      claimLoop.current?.abort();
      claimLoop.current = null;
    };
  }, [refresh, startClaimWatch]);

  useEffect(() => {
    const pathChanged = lastPath.current !== null && lastPath.current !== pathname;
    lastPath.current = pathname;
    if (pathChanged || (lastToken.current !== undefined && accessToken() !== lastToken.current)) void refresh();
  }, [pathname, sessionToken, refresh]);

  const value = useMemo<FunnelValue>(
    () => ({
      status,
      signature: signature(status),
      revealedRungs: revealedRungs(status),
      doc: (sku) => vaultDocument(status, sku),
      refresh,
    }),
    [status, refresh],
  );

  return <FunnelContext.Provider value={value}>{children}</FunnelContext.Provider>;
}
